import dayjs, { type Dayjs } from 'dayjs';
import type { PayrollCycleConfig } from '../app/features/employee/emp-payroll/emp-payroll.type';

export interface ResolvedCycle {
  start: Dayjs;
  end: Dayjs;
  /** Number of days the cycle covers, inclusive. */
  days: number;
  /** Legacy batch label — always sent alongside the resolved period. */
  year: number;
  /** Zero padded month, e.g. `'03'`. */
  month: string;
  /** Human label, e.g. `14 Mar 2026 to 15 Apr 2026`. */
  label: string;
}

const DISPLAY = 'DD MMM YYYY';

/** Clamps a day-of-month to the given month's length (31 → 28/29/30). */
const clampDay = (base: Dayjs, day: number) =>
  base.date(Math.min(Math.max(day, 1), base.daysInMonth()));

/**
 * Turns a cycle config + the month the admin picked into a concrete period.
 *
 * CALENDAR_MONTH → 1st to last day of the picked month (today's behaviour)
 * CUSTOM_MONTH   → start_on_date of the picked month to the day before the
 *                  next cycle opens (e.g. 14 Mar to 13 Apr)
 * FIXED_INTERVAL → start_on_date (or the 1st) plus cycle_length_days
 */
export const resolveCycle = (
  config: PayrollCycleConfig | undefined | null,
  anchorMonth: Dayjs | string | Date | null | undefined,
): ResolvedCycle => {
  const anchor = dayjs(anchorMonth || undefined).startOf('month');
  const type = config?.cycle_type || 'CALENDAR_MONTH';

  let start = anchor.startOf('month');
  let end = anchor.endOf('month').startOf('day');

  if (type === 'CUSTOM_MONTH') {
    start = clampDay(anchor, Number(config?.start_on_date || 1));
    // The cycle runs to the day before the next one opens:
    // 14 Sep → 13 Oct, 1 Sep → 30 Sep. Month lengths take care of themselves.
    end = start.add(1, 'month').subtract(1, 'day');
  }

  if (type === 'FIXED_INTERVAL') {
    const interval = Math.max(Number(config?.cycle_length_days || 15), 1);
    start = clampDay(anchor, Number(config?.start_on_date || 1));
    end = start.add(interval - 1, 'day');
  }

  const labelAnchor = start;

  return {
    start,
    end,
    days: end.diff(start, 'day') + 1,
    year: Number(labelAnchor.format('YYYY')),
    month: labelAnchor.format('MM'),
    label: `${start.format(DISPLAY)} to ${end.format(DISPLAY)}`,
  };
};

const toResolved = (
  start: Dayjs,
  end: Dayjs,
  _config?: PayrollCycleConfig | null,
): ResolvedCycle => {
  const labelAnchor = start;
  return {
    start,
    end,
    days: end.diff(start, 'day') + 1,
    year: Number(labelAnchor.format('YYYY')),
    month: labelAnchor.format('MM'),
    label: `${start.format(DISPLAY)} to ${end.format(DISPLAY)}`,
  };
};

/**
 * Every cycle that STARTS inside the given month.
 *
 * A calendar month or a custom day range produces exactly one. A fixed interval
 * produces several — a 15-day cycle gives `01-15` and `16-30`, a 7-day cycle
 * gives four or five — which is what the admin has to choose between.
 */
export const cyclesInMonth = (
  config: PayrollCycleConfig | undefined | null,
  anchorMonth: Dayjs | string | Date | null | undefined,
): ResolvedCycle[] => {
  const month = dayjs(anchorMonth || undefined).startOf('month');

  if ((config?.cycle_type || 'CALENDAR_MONTH') !== 'FIXED_INTERVAL') {
    return [resolveCycle(config, month)];
  }

  const interval = Math.max(Number(config?.cycle_length_days || 15), 1);
  const cycles: ResolvedCycle[] = [];
  let start = clampDay(month, Number(config?.start_on_date || 1));

  // Guard the loop: a 1-day interval over a 31-day month is the worst case.
  while (start.isSame(month, 'month') && cycles.length < 40) {
    const end = start.add(interval - 1, 'day');
    cycles.push(toResolved(start, end, config));
    start = end.add(1, 'day');
  }

  return cycles.length ? cycles : [resolveCycle(config, month)];
};

/**
 * The cycle a given date falls inside. Used for prev/next navigation, so the
 * arrows step one cycle at a time instead of one month at a time.
 */
export const resolveCycleContaining = (
  config: PayrollCycleConfig | undefined | null,
  date: Dayjs | string | Date | null | undefined,
): ResolvedCycle => {
  const target = dayjs(date || undefined).startOf('day');
  const type = config?.cycle_type || 'CALENDAR_MONTH';

  if (type === 'CALENDAR_MONTH') return resolveCycle(config, target);

  // Look at this month and the one before it — a cycle can start in either.
  const candidates = [
    ...cyclesInMonth(config, target.subtract(1, 'month')),
    ...cyclesInMonth(config, target),
  ];

  const match = candidates.find(
    (cycle) =>
      !target.isBefore(cycle.start, 'day') && !target.isAfter(cycle.end, 'day'),
  );

  return match || resolveCycle(config, target);
};

/** The cycle immediately before / after the given one. */
export const stepCycle = (
  config: PayrollCycleConfig | undefined | null,
  cycle: ResolvedCycle,
  direction: -1 | 1,
): ResolvedCycle =>
  resolveCycleContaining(
    config,
    direction === 1 ? cycle.end.add(1, 'day') : cycle.start.subtract(1, 'day'),
  );

/**
 * Period text for a batch row. Falls back to the legacy `year`/`month` pair so
 * batches created before the calendar existed keep rendering.
 */
export const formatBatchPeriod = (row: {
  cycle_start_date?: string | null;
  cycle_end_date?: string | null;
  year?: number | string;
  month?: string;
}): string => {
  if (row?.cycle_start_date && row?.cycle_end_date) {
    return `${dayjs(row.cycle_start_date).format(DISPLAY)} to ${dayjs(row.cycle_end_date).format(DISPLAY)}`;
  }
  if (row?.year && row?.month) {
    return dayjs(`${row.year}-${row.month}-01`).format('MMM YYYY');
  }
  return 'n/a';
};

/** Every day in the cycle — used by the Daily Pay Sheet grid. */
export const cycleDays = (cycle: ResolvedCycle): Dayjs[] =>
  Array.from({ length: cycle.days }, (_, i) => cycle.start.add(i, 'day'));
